import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_VIEW,
  clampView,
  clientPointToMap,
  easeInOutCubic,
  fitBounds,
  lerpView,
  panBy,
  pixelsToMapUnits,
  viewsEqual,
  zoomBy,
  type Bounds,
  type MapView,
} from "@/lib/map/view";

/**
 * The pan/zoom controller: a hand-rolled SVG viewBox camera.
 *
 * Deliberately not d3-zoom — the maths it would give us is ~120 lines of pure
 * functions in src/lib/map/view.ts (already unit-tested), and d3-zoom would add a
 * client dependency plus its own event model to fight with React's. Everything
 * stateful lives here; everything computable lives there.
 *
 * Supports drag-pan, wheel-zoom toward the cursor, two-finger pinch, and — via
 * onKeyDown — arrow-key pan, +/- zoom and 0 to reset.
 */

const KEY_PAN_FRACTION = 0.18; // of the visible width/height per press
const WHEEL_SENSITIVITY = 0.0016;
const FLIGHT_MS = 520;

export interface MapCamera {
  view: MapView;
  /** True while a fly-to is running — used to suppress hover work mid-flight. */
  flying: boolean;
  /** True while a pointer drag is in progress — used to suppress the click that follows. */
  dragging: boolean;
  setView: (next: MapView) => void;
  flyTo: (target: MapView) => void;
  flyToBounds: (bounds: Bounds, padding?: number) => void;
  reset: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  onKeyDown: (event: React.KeyboardEvent) => boolean;
  /** Attach to the <svg>: pointer handlers for drag and pinch. */
  surfaceRef: React.RefObject<SVGSVGElement | null>;
  pointerHandlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function useMapView(initial: MapView = DEFAULT_VIEW): MapCamera {
  const [view, setViewState] = useState<MapView>(() => clampView(initial));
  const [flying, setFlying] = useState(false);
  const [dragging, setDragging] = useState(false);

  const surfaceRef = useRef<SVGSVGElement | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  const frameRef = useRef<number | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ distance: number; midpoint: { x: number; y: number } } | null>(null);
  const draggedRef = useRef(false);

  const cancelFlight = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    setFlying(false);
  }, []);

  useEffect(() => cancelFlight, [cancelFlight]);

  const setView = useCallback(
    (next: MapView) => {
      cancelFlight();
      setViewState(clampView(next));
    },
    [cancelFlight],
  );

  /** Ease to a target view. Honours prefers-reduced-motion by jumping instead. */
  const flyTo = useCallback(
    (target: MapView) => {
      const to = clampView(target);
      const from = viewRef.current;
      cancelFlight();
      if (viewsEqual(from, to)) return;
      if (prefersReducedMotion()) {
        setViewState(to);
        return;
      }
      const start = performance.now();
      setFlying(true);
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / FLIGHT_MS);
        setViewState(lerpView(from, to, easeInOutCubic(t)));
        if (t < 1) {
          frameRef.current = requestAnimationFrame(step);
        } else {
          frameRef.current = null;
          setFlying(false);
        }
      };
      frameRef.current = requestAnimationFrame(step);
    },
    [cancelFlight],
  );

  const flyToBounds = useCallback(
    (bounds: Bounds, padding?: number) => flyTo(fitBounds(bounds, padding)),
    [flyTo],
  );

  const reset = useCallback(() => flyTo(DEFAULT_VIEW), [flyTo]);
  const zoomIn = useCallback(() => setView(zoomBy(viewRef.current, 1.6)), [setView]);
  const zoomOut = useCallback(() => setView(zoomBy(viewRef.current, 1 / 1.6)), [setView]);

  // Wheel zoom has to be a non-passive native listener: React attaches wheel
  // listeners passively, so preventDefault() from onWheel would be ignored and the
  // page would scroll behind the map.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      cancelFlight();
      const rect = el.getBoundingClientRect();
      const focus = clientPointToMap(viewRef.current, { x: event.clientX, y: event.clientY }, rect);
      // Normalize line/page deltas to something comparable to pixel deltas.
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
      const factor = Math.exp(-event.deltaY * unit * WHEEL_SENSITIVITY);
      setViewState(zoomBy(viewRef.current, factor, focus));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [cancelFlight]);

  const pointerHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      const el = surfaceRef.current;
      if (!el) return;
      // Let the browser handle a real click on a feature link; only claim the
      // pointer once it actually moves (see onPointerMove).
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      draggedRef.current = false;
      if (pointersRef.current.size === 2) {
        const [a, b] = [...pointersRef.current.values()];
        pinchRef.current = {
          distance: Math.hypot(a.x - b.x, a.y - b.y),
          midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        };
      }
      cancelFlight();
    },
    onPointerMove: (e: React.PointerEvent) => {
      const el = surfaceRef.current;
      const pointers = pointersRef.current;
      const previous = pointers.get(e.pointerId);
      if (!el || !previous) return;
      const rect = el.getBoundingClientRect();
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const pinch = pinchRef.current;
        if (pinch && pinch.distance > 0 && distance > 0) {
          const focus = clientPointToMap(viewRef.current, midpoint, rect);
          setViewState(zoomBy(viewRef.current, distance / pinch.distance, focus));
        }
        pinchRef.current = { distance, midpoint };
        draggedRef.current = true;
        setDragging(true);
        return;
      }

      const dxPixels = e.clientX - previous.x;
      const dyPixels = e.clientY - previous.y;
      if (!draggedRef.current && Math.hypot(dxPixels, dyPixels) < 3) return; // still a click
      draggedRef.current = true;
      setDragging(true);
      if (el.hasPointerCapture?.(e.pointerId) === false) el.setPointerCapture?.(e.pointerId);
      const size = { width: rect.width, height: rect.height };
      setViewState(
        panBy(
          viewRef.current,
          -pixelsToMapUnits(viewRef.current, dxPixels, size),
          -pixelsToMapUnits(viewRef.current, dyPixels, size),
        ),
      );
    },
    onPointerUp: (e: React.PointerEvent) => {
      pointersRef.current.delete(e.pointerId);
      if (pointersRef.current.size < 2) pinchRef.current = null;
      if (pointersRef.current.size === 0) {
        // Leave `dragging` true until after the click event this release triggers,
        // so a drag that ends over a state doesn't also open that state.
        if (draggedRef.current) requestAnimationFrame(() => setDragging(false));
        else setDragging(false);
      }
      surfaceRef.current?.releasePointerCapture?.(e.pointerId);
    },
    onPointerCancel: (e: React.PointerEvent) => {
      pointersRef.current.delete(e.pointerId);
      pinchRef.current = null;
      setDragging(false);
    },
  };

  /** Returns true when the key was handled, so the caller can preventDefault(). */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent): boolean => {
      const current = viewRef.current;
      const stepX = (960 / current.scale) * KEY_PAN_FRACTION;
      const stepY = (600 / current.scale) * KEY_PAN_FRACTION;
      switch (event.key) {
        case "ArrowLeft":
          setView(panBy(current, -stepX, 0));
          return true;
        case "ArrowRight":
          setView(panBy(current, stepX, 0));
          return true;
        case "ArrowUp":
          setView(panBy(current, 0, -stepY));
          return true;
        case "ArrowDown":
          setView(panBy(current, 0, stepY));
          return true;
        case "+":
        case "=":
          zoomIn();
          return true;
        case "-":
        case "_":
          zoomOut();
          return true;
        case "0":
          reset();
          return true;
        default:
          return false;
      }
    },
    [reset, setView, zoomIn, zoomOut],
  );

  return {
    view,
    flying,
    dragging,
    setView,
    flyTo,
    flyToBounds,
    reset,
    zoomIn,
    zoomOut,
    onKeyDown,
    surfaceRef,
    pointerHandlers,
  };
}
